"use client";

import {
  useEffect,
  createContext,
  useContext,
  useState,
  useCallback,
  useRef,
} from "react";
import { useUserStore } from "@/store/userStore";
import { createClient } from "@/lib/supabase/client";
import { useRouter, usePathname } from "@/i18n/navigation";
import { type Session } from "@supabase/supabase-js";
import { useLocale } from "next-intl";
import { routing } from "@/i18n/routing";
import {
  profileLanguageToLocale,
  localeToProfileLanguage,
} from "@/lib/utils/language";
import { languageLogger } from "@/lib/utils/debug-logger";
import { identifyUser, resetMixpanel } from "@/lib/mixpanel-client";

type AuthContextType = {
  session: Session | null;
  isLoading: boolean;
  refreshSession: () => Promise<void>;
};

const AuthContext = createContext<AuthContextType>({
  session: null,
  isLoading: true,
  refreshSession: async () => {},
});

export const useAuth = () => useContext(AuthContext);

export function UserProvider({ children }: { children: React.ReactNode }) {
  const { initialize, profile, updatePreferences } = useUserStore();
  const supabase = createClient();
  const router = useRouter();
  const pathname = usePathname();
  const currentLocale = useLocale();
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Use refs to track whether we're currently redirecting or syncing
  // to prevent infinite loops
  const isRedirecting = useRef(false);
  const isSyncingPreference = useRef(false);
  const initialSessionJustHandledRef = useRef(false);

  // Add a ref to store if we're in a menu page to prevent unnecessary server interaction
  const isMenuPage = useRef(false);

  const refreshSession = useCallback(async () => {
    try {
      const {
        data: { session: currentSession },
      } = await supabase.auth.getSession();
      setSession(currentSession);
      if (currentSession) {
        initialize(supabase).catch(console.error);
      }
    } catch (error) {
      console.error("Error refreshing session:", error);
    }
  }, [supabase, initialize]);

  // Update isMenuPage ref whenever pathname changes
  useEffect(() => {
    if (pathname) {
      isMenuPage.current = pathname.includes("/m/");
    }
  }, [pathname]);

  // Effect to handle language preference and URL
  // This handles: profile preference -> URL locale sync
  useEffect(() => {
    if (
      isRedirecting.current ||
      isSyncingPreference.current ||
      isMenuPage.current
    )
      return;

    // Skip language preference redirection for menu pages
    // Moved the isMenuPage check to the top guard condition for clarity

    if (!isLoading && profile && pathname && !pathname.includes("/auth/")) {
      // ONLY act if there's an explicit language preference set in the profile
      if (profile.language_preference) {
        const preferredLanguage = profile.language_preference; // No "|| 'english'" default here
        const preferredLocale = profileLanguageToLocale(preferredLanguage);

        // Only redirect if the current locale is different from the preferred locale
        // and the preferred locale is supported
        if (
          preferredLocale &&
          routing.locales.includes(preferredLocale as "en" | "ja") &&
          currentLocale !== preferredLocale
        ) {
          languageLogger.log(
            `Redirecting to preferred language: ${preferredLocale} based on profile setting.`
          );
          isRedirecting.current = true;

          // Use setTimeout to break the cycle of synchronous renders
          setTimeout(() => {
            try {
              router.replace(pathname, {
                locale: preferredLocale as "en" | "ja",
              });
            } catch (error) {
              languageLogger.error(
                "Navigation error during language redirect",
                error
              );
            } finally {
              // Reset after a short delay to ensure the navigation has time to start
              setTimeout(() => {
                isRedirecting.current = false;
              }, 100);
            }
          }, 0);
        }
      }
      // If profile.language_preference is null/undefined, this hook does nothing.
      // The other hook (URL -> Profile sync) will handle setting the initial preference
      // based on the current URL's locale.
    }
  }, [profile, router, pathname, isLoading, currentLocale]);

  // Effect to sync locale with user preference if changed via LocaleSwitcher
  // This handles: URL locale -> profile preference sync
  useEffect(() => {
    if (isRedirecting.current || isSyncingPreference.current) return;

    // Skip preference updates for menu pages - allows temporary language switching on menus
    if (isMenuPage.current) {
      return;
    }

    const syncLocaleToUserPreference = async () => {
      if (
        profile &&
        !isLoading &&
        currentLocale &&
        profile.language_preference !== localeToProfileLanguage(currentLocale)
      ) {
        languageLogger.log(
          `Syncing locale ${currentLocale} to user preference`,
          {
            currentPreference: profile.language_preference,
            newPreference: localeToProfileLanguage(currentLocale),
          }
        );

        isSyncingPreference.current = true;

        try {
          // Only update preferences if not in a menu page
          if (!isMenuPage.current) {
            await updatePreferences(
              { language_preference: localeToProfileLanguage(currentLocale) },
              supabase
            );
          }
        } catch (error) {
          languageLogger.error(
            "Error syncing locale to user preference",
            error
          );
        } finally {
          isSyncingPreference.current = false;
        }
      }
    };

    // Only sync preferences on non-menu pages
    if (!isMenuPage.current) {
      syncLocaleToUserPreference();
    }
  }, [
    currentLocale,
    profile,
    isLoading,
    updatePreferences,
    supabase,
    pathname,
  ]);

  useEffect(() => {
    let mounted = true;

    const initializeAuth = async () => {
      try {
        const {
          data: { session: initialSession },
        } = await supabase.auth.getSession();

        if (mounted) {
          setSession(initialSession);
          if (initialSession) {
            // Identify user in Mixpanel when session exists
            identifyUser(initialSession.user.id);
            initialize(supabase).catch(console.error);
          }
          setIsLoading(false);
        }

        const {
          data: { subscription },
        } = supabase.auth.onAuthStateChange(async (event, currentSession) => {
          if (!mounted) return;

          const oldSessionUserId = session?.user?.id;
          console.log(
            "Auth event:",
            event,
            "Old Session User ID:",
            oldSessionUserId,
            "Current Session User ID:",
            currentSession?.user?.id,
            "InitialFlag:",
            initialSessionJustHandledRef.current
          );

          setSession(currentSession);

          if (event === "INITIAL_SESSION") {
            if (currentSession) {
              identifyUser(currentSession.user.id);
              initialSessionJustHandledRef.current = true;
            }
            await initialize(supabase);
          } else if (event === "SIGNED_IN") {
            if (currentSession) {
              identifyUser(currentSession.user.id);
            }
            await initialize(supabase);

            let shouldRefresh = false;
            if (currentSession && oldSessionUserId !== currentSession.user.id) {
              // User has changed or was previously null
              if (
                initialSessionJustHandledRef.current &&
                oldSessionUserId === undefined
              ) {
                // This SIGNED_IN immediately followed INITIAL_SESSION for a new session.
                console.log(
                  "Skipping router.refresh for SIGNED_IN immediately after INITIAL_SESSION"
                );
                shouldRefresh = false;
              } else {
                // This is a genuine new login or user switch after initial load.
                shouldRefresh = true;
              }
            }
            // Reset the flag after its use in this SIGNED_IN event processing
            initialSessionJustHandledRef.current = false;

            if (shouldRefresh) {
              router.refresh();
            }
          } else if (event === "SIGNED_OUT") {
            resetMixpanel();
            if (pathname.startsWith("/dashboard")) {
              router.push("/" as const);
            }
          } else if (event === "TOKEN_REFRESHED" || event === "USER_UPDATED") {
            if (currentSession) {
              identifyUser(currentSession.user.id);
            }
            await initialize(supabase);
            if (event === "USER_UPDATED") {
              router.refresh();
            }
          }
        });

        return () => {
          mounted = false;
          subscription.unsubscribe();
        };
      } catch (error) {
        console.error("Error initializing auth:", error);
        if (mounted) {
          setIsLoading(false);
        }
      }
    };

    initializeAuth();
  }, [initialize, supabase, router, pathname]);

  return (
    <AuthContext.Provider value={{ session, isLoading, refreshSession }}>
      {children}
    </AuthContext.Provider>
  );
}
